import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { finalize, takeUntil } from "rxjs/operators";
import { SubscribingComponent } from "app/common";
import { Statement } from "app/statements";
import { StatementService } from "app/statements/statement.service";
import { DocumentRequest, DocumentRequestResponse } from "app/statements/document-request";
import { TranslateService } from "@ngx-translate/core";

interface DocumentRequestDialogData {
  statement: Statement;
}

@Component({
  selector: "app-document-request-dialog",
  templateUrl: "./document-request-dialog.component.html",
  styleUrls: ["./document-request-dialog.component.scss"],
  standalone: false
})
export class DocumentRequestDialogComponent extends SubscribingComponent implements OnInit {
  public documentRequest: DocumentRequest | null = null;
  public isLoading = false;
  public isSubmitting = false;
  public errorMessage = "";
  public clientNote = "";

  private isClientNoteDirty = false;
  private uploadingItemIds: Set<number> = new Set<number>();
  private deletingFileIds: Set<number> = new Set<number>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DocumentRequestDialogData,
    private dialogRef: MatDialogRef<DocumentRequestDialogComponent>,
    private statementService: StatementService,
    private translate: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadDocumentRequest();
  }

  public close(): void {
    this.dialogRef.close();
  }

  public onClientNoteChange(note: string): void {
    this.clientNote = note;
    this.isClientNoteDirty = true;
  }

  public onFilesSelected(itemId: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);

    input.value = "";
    if (!files.length || !this.statementId) return;

    this.errorMessage = "";
    this.uploadingItemIds.add(itemId);

    this.statementService
      .uploadDocumentRequestFiles(this.statementId, itemId, files)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => this.uploadingItemIds.delete(itemId))
      )
      .subscribe({
        next: () => this.loadDocumentRequest(),
        error: (error: any) => {
          this.errorMessage = this.extractErrorMessage(error);
        }
      });
  }

  public removeFile(fileId: number): void {
    if (!this.statementId) return;

    this.errorMessage = "";
    this.deletingFileIds.add(fileId);

    this.statementService
      .deleteDocumentRequestFile(this.statementId, fileId)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => this.deletingFileIds.delete(fileId))
      )
      .subscribe({
        next: () => this.loadDocumentRequest(),
        error: (error: any) => {
          this.errorMessage = this.extractErrorMessage(error);
        }
      });
  }

  public submitDocumentRequest(): void {
    if (!this.statementId || !this.documentRequest) return;

    this.errorMessage = "";
    this.isSubmitting = true;

    this.statementService
      .submitDocumentRequest(this.statementId, this.clientNote?.trim() || "")
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => (this.isSubmitting = false))
      )
      .subscribe({
        next: () => {
          this.dialogRef.close({ submitted: true });
        },
        error: (error: any) => {
          this.errorMessage = this.extractErrorMessage(error);
        }
      });
  }

  public isItemUploading(itemId: number): boolean {
    return this.uploadingItemIds.has(itemId);
  }

  public isFileDeleting(fileId: number): boolean {
    return this.deletingFileIds.has(fileId);
  }

  public get isSubmitDisabled(): boolean {
    return this.isSubmitting || this.isLoading || !this.documentRequest || !this.hasAnyUploadedFiles;
  }

  private get hasAnyUploadedFiles(): boolean {
    if (!this.documentRequest?.items?.length) return false;
    return this.documentRequest.items.some((item) => (item.files?.length || 0) > 0);
  }

  private get statementId(): number | null {
    return this.data?.statement?.id || null;
  }

  private loadDocumentRequest(): void {
    if (!this.statementId) {
      this.documentRequest = null;
      return;
    }

    this.isLoading = true;
    this.errorMessage = "";

    this.statementService
      .getDocumentRequest(this.statementId)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (response: DocumentRequestResponse) => {
          this.documentRequest = response?.document_request || null;
          if (!this.isClientNoteDirty) {
            this.clientNote = this.documentRequest?.client_note || "";
          }
        },
        error: (error: any) => {
          this.documentRequest = null;
          this.errorMessage = this.extractErrorMessage(error);
        }
      });
  }

  private extractErrorMessage(error: any): string {
    const message = this.parseErrorValue(error?.error);
    return message || this.translate.instant("DOCUMENT_REQUEST.ERROR_GENERIC");
  }

  private parseErrorValue(value: unknown): string {
    if (!value) return "";
    if (typeof value === "string") return value;

    if (Array.isArray(value)) {
      for (const currentValue of value) {
        const message = this.parseErrorValue(currentValue);
        if (message) return message;
      }

      return "";
    }

    if (typeof value === "object") {
      const record = value as Record<string, unknown>;
      const importantFields = ["detail", "message", "error", "non_field_errors"];

      for (const fieldName of importantFields) {
        if (record[fieldName] !== undefined) {
          const message = this.parseErrorValue(record[fieldName]);
          if (message) return message;
        }
      }

      for (const fieldName of Object.keys(record)) {
        const message = this.parseErrorValue(record[fieldName]);
        if (message) return message;
      }
    }

    return "";
  }
}
